import { ApiError } from '../lib/api';
import { t, type TranslationKey } from '../lib/i18n';

/**
 * The inline counterpart of Blocking: a failure a view can recover from
 * without losing the screen around it.
 *
 * COPY IS KEYED BY THE SERVER'S OWN ERROR CODE (`error.<code>`, the convention
 * stated in lib/api.ts). That is what makes "is every error handled?" a diff
 * between two lists rather than an audit: the taxonomy in api.ts and the
 * error.* block in de.json can be compared line by line.
 *
 * A code with no copy yet must not render `error.no_such_thing` at an owner,
 * so the resolver checks for the key's ABSENCE using t()'s documented
 * behaviour (a missing key returns the key itself) and falls back. The
 * fallback is deliberately generic: guessing at a message for an unknown code
 * is how a UI ends up asserting something the server never said.
 */
export function errorMessage(error: unknown, fallback?: string): string {
  // THE ONE CODE THAT IS NOT KEYED OFF THE SERVER'S CODE (final review I-1).
  // `kind: 'auth'` is decided by STATUS in lib/api.ts, so this answers for a
  // 401 that carried no JSON body at all -- where `code` is null and the
  // resolver below would fall through to "Der Vorgang ist fehlgeschlagen.",
  // the generic sentence that sends an operator looking for a broken server
  // instead of a login form. Checked BEFORE the code lookup, not after, so it
  // cannot be reached only for the well-formed half of the cases.
  //
  // A literal t() call, not the `error.<code>` path: test/i18nKeys.test.ts
  // reads literal single-quoted calls, and a key nothing names literally is a
  // key it reports as dead copy. queries.ts's `reportAuthFailure` is the other
  // half -- this sentence is only honest because the shell above it is coming
  // down.
  if (error instanceof ApiError && error.kind === 'auth') return t('error.unauthenticated');
  const code = error instanceof ApiError ? error.code : null;
  if (code) {
    const key = `error.${code}`;
    const resolved = t(key as TranslationKey);
    if (resolved !== key) return resolved;
  }
  return fallback ?? t('error.generic');
}

export function ErrorNotice({ error, fallback }: { error: unknown; fallback?: string }) {
  return (
    <p className="error-notice" role="alert">
      {errorMessage(error, fallback)}
    </p>
  );
}
